/**
 * ============================================================================
 * RetentionMatrixAdmin — Phase 6a frontend (T-P6a-W3-01)
 * Owner: Claude 3 (Frontend & UX).
 * Route: /retention/matrix
 * Purpose: per doc-type retention rules that drive the records-disposition
 *          workflow (DESTROY / ARCHIVE / EXPORT). One row per (doc_type,
 *          policy); GMP-Critical activation routes through dual signature.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Empty state has a primary CTA + governance copy.
 *   - Loading state shows skeleton rows (no spinner-only).
 *   - Error state shows a refined envelope with a Retry button — never an
 *     uncaught toast.
 *   - Activation collects TWO signature bundle ids; UI hands them to the
 *     server which enforces "distinct signers".
 *
 * URS rows: RET-001..007, SIG-011 (dual-sign meanings).
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './RetentionMatrixAdmin.module.css';
import { usePoliciesQuery, type RetentionPolicy } from './usePoliciesQuery';
import { useActivatePolicyMutation } from './useActivatePolicyMutation';
import { PolicyMatrixGrid } from './PolicyMatrixGrid';
import { PolicyFormDialog } from './PolicyFormDialog';

type DialogMode = { kind: 'closed' } | { kind: 'form'; policy?: RetentionPolicy } | { kind: 'activate'; policy: RetentionPolicy };

export function RetentionMatrixAdmin() {
  const { t, i18n } = useTranslation();
  const policiesQ = usePoliciesQuery();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const [dialog, setDialog] = useState<DialogMode>({ kind: 'closed' });

  const items: RetentionPolicy[] = policiesQ.data?.items ?? [];
  const isEmpty = !policiesQ.isLoading && !policiesQ.isError && items.length === 0;

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('retention.matrix.eyebrow', { defaultValue: 'Quality' })}
        title={t('retention.matrix.title', {
          defaultValue: 'Retention Policy Matrix',
        })}
        subtitle={t('retention.matrix.subtitle', {
          defaultValue:
            'Per doc-type retention rules driving the records-disposition workflow.',
        })}
        actions={
          <Button
            variant="primary"
            leadingIcon="+"
            onClick={() => setDialog({ kind: 'form' })}
            aria-label={t('retention.matrix.actions.newAria', {
              defaultValue: 'Create a new retention policy',
            })}
          >
            {t('retention.matrix.actions.new', { defaultValue: 'New Policy' })}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <h2 className={wstyles.sectionTitle}>
            {t('retention.matrix.list.title', { defaultValue: 'Policies' })}{' '}
            <span style={{ color: 'var(--color-text-muted)' }}>
              ({nf.format(items.length)})
            </span>
          </h2>
        </CardHeader>
        <CardBody>
          {policiesQ.isLoading && (
            <div role="status" aria-live="polite" aria-label={t('common.loading', { defaultValue: 'Loading…' })}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          )}

          {policiesQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('retention.matrix.errors.loadFailed', {
                  defaultValue: 'Could not load retention policies.',
                })}
              </span>
              <Button variant="secondary" size="sm" onClick={() => policiesQ.refetch()}>
                {t('retention.matrix.actions.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {isEmpty && (
            <div className={styles.emptyState}>
              <h3 className={styles.emptyTitle}>
                {t('retention.matrix.empty.title', {
                  defaultValue: 'No retention policies yet.',
                })}
              </h3>
              <p>
                {t('retention.matrix.empty.body', {
                  defaultValue: 'Add one to start governing GxP retention.',
                })}
              </p>
              <Button
                variant="primary"
                leadingIcon="+"
                onClick={() => setDialog({ kind: 'form' })}
              >
                {t('retention.matrix.actions.new', { defaultValue: 'New Policy' })}
              </Button>
            </div>
          )}

          {!policiesQ.isLoading && !policiesQ.isError && items.length > 0 && (
            <PolicyMatrixGrid
              policies={items}
              onEdit={(p) => setDialog({ kind: 'form', policy: p })}
              onActivate={(p) => setDialog({ kind: 'activate', policy: p })}
              onViewHistory={(p) => {
                // History view is a follow-on task (T-P6a-W3-02). For now we
                // surface a stub message — never leave a button silently dead.
                window.alert(
                  t('retention.matrix.history.stub', {
                    defaultValue: 'History view: policy {{id}} (v{{v}})',
                    id: p.policy_id,
                    v: p.version,
                  }),
                );
              }}
            />
          )}
        </CardBody>
      </Card>

      {dialog.kind === 'form' && (
        <PolicyFormDialog
          initial={dialog.policy}
          onClose={() => setDialog({ kind: 'closed' })}
          onSaved={() => {
            setDialog({ kind: 'closed' });
            policiesQ.refetch();
          }}
        />
      )}

      {dialog.kind === 'activate' && (
        <ActivatePolicyDialog
          policy={dialog.policy}
          onClose={() => setDialog({ kind: 'closed' })}
          onActivated={() => {
            setDialog({ kind: 'closed' });
            policiesQ.refetch();
          }}
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * ActivatePolicyDialog — collects two distinct signature bundle ids and
 * forwards them to the dual-sign activation endpoint. This is the same
 * dual-sign shape used by TenantConfigAdmin (GMP_CRITICAL keys) — the server
 * enforces "distinct signer" so we keep the UI simple and let it be the
 * source of truth.
 * --------------------------------------------------------------------------- */

interface ActivatePolicyDialogProps {
  policy: RetentionPolicy;
  onClose: () => void;
  onActivated: () => void;
}

function ActivatePolicyDialog({ policy, onClose, onActivated }: ActivatePolicyDialogProps) {
  const { t } = useTranslation();
  const activateM = useActivatePolicyMutation();
  const [sigA, setSigA] = useState('');
  const [sigB, setSigB] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!sigA.trim() || !sigB.trim()) {
      setError(t('retention.matrix.errors.dualSignRequired', {
        defaultValue: 'Two signature bundle ids are required.',
      }));
      return;
    }
    if (sigA.trim() === sigB.trim()) {
      setError(t('retention.matrix.errors.dualSignDistinct', {
        defaultValue: 'The two signers must be different.',
      }));
      return;
    }
    try {
      await activateM.mutateAsync({
        policyId: policy.policy_id,
        signatureBundleIdA: sigA.trim(),
        signatureBundleIdB: sigB.trim(),
      });
      onActivated();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('retention.matrix.errors.serverFailed', {
        defaultValue: 'Server rejected the change.',
      }));
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rp-activate-title"
        aria-describedby="rp-activate-desc"
      >
        <header className={dialogStyles.header}>
          <h2 id="rp-activate-title" className={dialogStyles.title}>
            {t('retention.matrix.activate.title', {
              defaultValue: 'Activate retention policy',
            })}
          </h2>
          <p id="rp-activate-desc" className={dialogStyles.desc}>
            {t('retention.matrix.activate.subtitle', {
              defaultValue:
                'This is a GMP-Critical change. Activation requires two distinct signature bundles (RETENTION_POLICY_DUAL_SIGN_A and _B). The server rejects duplicate signers.',
              policy: policy.name,
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={styles.dualSigWarn} role="note">
            <strong>{policy.name}</strong> · <code>{policy.doc_type_code}</code>{' '}
            · {policy.data_class}
          </div>

          <div className={styles.dualSigGrid}>
            <div className={dialogStyles.field}>
              <label htmlFor="rp-sig-a" className={dialogStyles.label}>
                {t('retention.matrix.activate.sigA', {
                  defaultValue: 'Signature bundle id — SIGNER A',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="rp-sig-a"
                className={dialogStyles.input}
                value={sigA}
                onChange={(e) => setSigA(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="rp-sig-b" className={dialogStyles.label}>
                {t('retention.matrix.activate.sigB', {
                  defaultValue: 'Signature bundle id — SIGNER B',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="rp-sig-b"
                className={dialogStyles.input}
                value={sigB}
                onChange={(e) => setSigB(e.target.value)}
                placeholder="sig-…"
                required
              />
            </div>
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={activateM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={activateM.isPending}>
              {t('retention.matrix.actions.activate', { defaultValue: 'Activate' })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
